import { useCallback, useContext, useEffect, useRef } from 'react';
import * as R from 'ramda';

import { Checkbox, Icon, List, Progress } from '@atom/mui';
import { FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { BudgetUnitSearch } from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetingUnitSelectionContext from './BudgetingUnitSelectionContext';

const { ListItem, ListItemText } = List;

const styles = {
  iconStyle: {
    fontSize: '1.75rem',
    margin: 0,
  },
  containerPadding: {
    margin: '1rem',
  },
  listItemText: {
    paddingLeft: '0.5rem',
  },
  textNoWrap: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  listItem: {
    cursor: 'pointer',
    maxWidth: '27rem',
  },
  optionsContainer: {
    maxHeight: '330px',
    overflow: 'auto',
  },
};
export interface Props {
  searchResults: BudgetUnitSearch[];
  loadingSearch: boolean;
  handlePageScroll: (nextPage: number) => void;
  page: number;
  total: number;
}

const BudgetingUnitSearchResults = ({
  searchResults,
  loadingSearch,
  handlePageScroll,
  page,
  total,
}: Props) => {
  const scrollContainerRef = useRef<any>(null);
  const { selectedUnitIds, handleChange, allUnits } = useContext(
    BudgetingUnitSelectionContext,
  );
  const handleScroll = useCallback(() => {
    const elem = scrollContainerRef?.current;
    const isBottom =
      elem?.scrollTop + elem?.clientHeight + 0.5 >= elem?.scrollHeight;
    if (isBottom && searchResults.length < total && !loadingSearch) {
      handlePageScroll(page + 1);
    }
  }, [
    scrollContainerRef.current,
    total,
    page,
    searchResults,
    handlePageScroll,
  ]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef?.current?.addEventListener('scroll', handleScroll);
      return () =>
        scrollContainerRef?.current?.removeEventListener(
          'scroll',
          handleScroll,
        );
    }
    return () => {};
  }, [handleScroll]);

  const handleCheckboxChange = (searchUnit: BudgetUnitSearch) => {
    const unitChanged = allUnits.find(unit => unit.id === searchUnit.id);
    if (isNilOrEmpty(unitChanged)) {
      return;
    }
    handleChange(unitChanged);
  };

  const getListItem = (unit: BudgetUnitSearch) => {
    const ancestorSelected = unit.ancestors.some(ancestor =>
      selectedUnitIds.has(ancestor.id),
    );
    return (
      <ListItem key={unit.id} style={styles.listItem}>
        <Checkbox
          checked={selectedUnitIds.has(unit.id) || ancestorSelected}
          onChange={() => handleCheckboxChange(unit)}
          disabled={ancestorSelected}
        />
        <Icon
          style={styles.iconStyle}
          color={colors.neutral.gray}
          icon={FolderIcon}
        />
        <ListItemText
          style={styles.listItemText}
          primary={unit.name}
          primaryTextStyle={styles.textNoWrap}
          secondary={unit.ancestors.map(({ name }) => name).join(' / ')}
          secondaryTextStyle={styles.textNoWrap}
        />
      </ListItem>
    );
  };

  return R.isNil(searchResults) ? (
    <Progress style={styles.containerPadding} size={20} />
  ) : R.isEmpty(searchResults) ? (
    <div style={styles.containerPadding}>no results</div>
  ) : (
    <div ref={scrollContainerRef} style={styles.optionsContainer}>
      <List>{searchResults.map(getListItem)}</List>
      {loadingSearch && (
        <div>
          <Progress size={20} />
        </div>
      )}
    </div>
  );
};

export default BudgetingUnitSearchResults;
